import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAlertContext } from '@/contexts/AlertContext'
import type { BidType, MarketProgram } from '@/features/bidding/constants'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import {
  formatMarketClosingTimeAsString,
  formatRemainingTimeAsString,
  getSecondsToMarketClosingWarning,
  getStateOfMarket,
  hasIntradayBidding,
  MarketState,
} from '@/features/bidding/utils/getStateOfMarket'

export function useMarketClosingWarning() {
  const { t } = useTranslation()
  const { pushAlert, resetAlerts } = useAlertContext()

  const showMarketClosingWarning = useCallback(
    (bidDay: MarketDate, marketProgram: MarketProgram, bidType: BidType) => {
      if (hasIntradayBidding(marketProgram, bidType)) {
        return
      }

      const stateOfMarket = getStateOfMarket(bidDay, marketProgram, bidType)

      switch (stateOfMarket) {
        case MarketState.CLOSING:
          pushAlert({
            severity: 'warning',
            message: t('bidding.market_closed.preemptive_warning', {
              time: formatMarketClosingTimeAsString(marketProgram),
              timeRemaining: formatRemainingTimeAsString(bidDay, marketProgram),
            }),
            isPersistent: false,
          })
          break
        case MarketState.CLOSED:
          pushAlert({
            severity: 'warning',
            message: t('bidding.market_closed.warning', {
              time: formatMarketClosingTimeAsString(marketProgram),
              timeRemaining: formatRemainingTimeAsString(bidDay, marketProgram),
            }),
            isPersistent: false,
          })
          break
        case MarketState.OPEN:
          setTimeout(
            () => {
              pushAlert({
                severity: 'warning',
                message: t('bidding.market_closed.preemptive_warning', {
                  time: formatMarketClosingTimeAsString(marketProgram),
                  timeRemaining: formatRemainingTimeAsString(bidDay, marketProgram),
                }),
                isPersistent: false,
              })
            },
            getSecondsToMarketClosingWarning(bidDay, marketProgram) * 1000,
          )
          break
      }
    },
    [pushAlert, t],
  )

  const clearMarketClosingWarning = useCallback(() => {
    resetAlerts()
  }, [resetAlerts])

  return useMemo(
    () => ({
      showMarketClosingWarning,
      clearMarketClosingWarning,
    }),
    [showMarketClosingWarning, clearMarketClosingWarning],
  )
}
